/**
 * EventLanding ✦ iter192 — Booth OS Era Alignment
 * ─────────────────────────────────────────────────────────────
 * The homepage stopped being a feature platform.
 *
 * What this page now communicates:
 *   1. The evening becomes a living system — it isn't software.
 *   2. Booth OS is the environment the evening enters,
 *      not a product within a platform.
 *   3. One QR → the entire evening.
 *   4. Four emotional layers (capture · leave · see · preserve)
 *      with the implicit "room responding" thread between them.
 *   5. The Keepsake · The Room · The Archive — moods, not tiers.
 *
 * Architectural rules (Craig, iter192 — DO NOT VIOLATE):
 *
 *   • No feature grids above the fold. Not even refined ones.
 *   • Emotionally legible before functionally legible. That order.
 *   • Motion is environmental — restrained opacity, soft drift,
 *     stagger rhythm. NO hover theatrics, NO kinetic scroll, NO
 *     reveal spam.
 *   • No section may visually peak louder than the hero.
 *   • Never call Booth OS "our platform". Use "the room", "the
 *     evening", "the shared space", "the experience".
 *   • Packages differ by whitespace density, vertical rhythm, line
 *     length, motion restraint, glow intensity — NOT primarily by
 *     colour.
 *   • Every section must emotionally decompress before the next
 *     begins. Generous vertical rhythm. Quiet exits.
 *   • Luxury implies depth. Do not over-explain atmosphere,
 *     archaeology, replay, or telemetry on this page.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import RoomReactsBridge from '../components/RoomReactsBridge';
import { EditorialFinalCTA, EditorialFooter } from '../components/HomepageEditorialSections';
import FeaturedReviewsCarousel from '../components/FeaturedReviewsCarousel';
import HomepageVenueMap from '../components/HomepageVenueMap';
import HomepageDynamicMemories from '../components/HomepageDynamicMemories';

// ── Constitutional tokens (locked, shared with HomepageEditorialSections) ──
const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const IVORY = '#FBEED0';
const IVORY_DIM = 'rgba(251,238,208,0.72)';
const IVORY_GHOST = 'rgba(251,238,208,0.48)';
const GOLD = '#D4AF5E';
const GOLD_MUTED = 'rgba(212,170,82,0.78)';
const GOLD_FAINT = 'rgba(212,170,82,0.18)';
const WALNUT = '#0E0805';
const WALNUT_ALT = '#120A06';
const WALNUT_DEEP = '#080503';

// ── PACKAGES manifest (constitutional source of truth) ──────
// Pinned by `test_editions_content_alignment.py` — the same items
// list shape that drives /pricing and the homepage editorial
// packages section. Every edition includes the FOUNDATION_ITEMS
// (Memory Snap · Memory Wall · Recap Letters · Archive Shelf) so
// the four invariant moments live in every package.
const FOUNDATION_ITEMS = ['Memory Snap', 'Memory Wall', 'Recap Letters', 'Archive Shelf'];

const PACKAGES = [
  {
    id: 'keepsake',
    name: 'The Keepsake ✦',
    price: '\u00A330',
    positioning: 'A quiet shared memory space.',
    caption: 'For an intimate evening that doesn’t need a stage.',
    cta: 'Purchase The Keepsake ✦',
    mood: 'intimate',
    items: [
      ...FOUNDATION_ITEMS,
      'Upload Memories',
      'Drop One Word',
      'Flip Guestbook',
      'Invites & RSVP',
    ],
  },
  {
    id: 'room',
    name: 'The Room ✦',
    price: '\u00A360',
    positioning: 'A live participation experience for the entire room.',
    caption: 'One QR quietly opens the evening.',
    cta: 'Purchase The Room ✦',
    mood: 'social',
    items: [
      'Booth OS',
      ...FOUNDATION_ITEMS,
      'Upload Memories',
      'Audio & Video Guestbook',
      'Flip Guestbook',
      'Lens Notes',
      'Drop One Word',
      'Pocket Booth',
      'Mega Missions',
      'Invites & RSVP',
    ],
    dimmed: ['Spin Film 360', 'Event Film', 'Highlight Reel'],
  },
  {
    id: 'archive',
    name: 'The Archive ✦',
    price: '\u00A399',
    positioning: 'A cinematic preservation system for the night.',
    caption: 'Composed. Archival. Complete. The evening, kept the way it deserves to be kept.',
    cta: 'Purchase The Archive ✦',
    mood: 'cinematic',
    items: [
      'Full Booth OS',
      ...FOUNDATION_ITEMS,
      'Upload Memories',
      'Audio & Video Guestbook',
      'Flip Guestbook',
      'Lens Notes',
      'Drop One Word',
      'Pocket Booth',
      'Spin Film 360',
      'Event Film',
      'Highlight Reel',
      'Invites & RSVP',
    ],
  },
];

// Lightweight CTA tracker (existing /api/demo/track endpoint).
const track = (event) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/demo/track`;
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* ignore */ }
};

// ── Motion presets (environmental, restrained) ──────────────
const calmRise = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 0.61, 0.36, 1] } },
};
const calmStagger = (delay = 0, gap = 0.14) => ({
  hidden: {},
  show: { transition: { delayChildren: delay, staggerChildren: gap } },
});

// =========================================================================
// PAGE
// =========================================================================
export default function EventLanding() {
  return (
    <div
      data-testid="homepage-root"
      style={{
        background: WALNUT,
        color: IVORY,
        fontFamily: SERIF,
        minHeight: '100vh',
        overflowX: 'hidden',
      }}
    >
      <Nav />
      <Hero />
      <PhilosophicalReset />
      <OneQrSection />
      <TransitionBridge
        copy="The evening quietly changes as people leave parts of themselves behind."
        testId="bridge-room-responding-1"
      />
      <FourEmotionalLayers />
      <RoomReactsBridge />
      <TransitionBridge
        copy="Not a tier. A mood. Choose the scale of the evening you want to keep."
        testId="bridge-package-prologue"
      />
      <Packages />
      <FeaturedReviewsCarousel
        variant="dark"
        filter="homepage"
        eyebrow="✦ Words from past evenings"
      />
      <HomepageDynamicMemories />
      <HomepageVenueMap />
      <SchoolsBridge />
      <EditorialFinalCTA />
      <EditorialFooter />
      <GlobalStyles />
    </div>
  );
}

// =========================================================================
// NAV — keep restrained, dissolves over hero
// =========================================================================
const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  // iter217 — Services ▾ dropdown for the new commercial vertical
  // (Photobooth Hire · Venues · Recent Events · For Schools mirror).
  // We keep the editorial homepage nav style; the dropdown is
  // a controlled menu, not a hover state, so mobile + touch work.
  const [servicesOpen, setServicesOpen] = useState(false);
  const servicesWrapRef = useRef(null);
  const location = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    const onClick = (e) => {
      if (servicesWrapRef.current && !servicesWrapRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  useEffect(() => { setServicesOpen(false); }, [location.pathname]);

  const quietLink = {
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 14,
    color: IVORY_DIM,
    textDecoration: 'underline',
    textDecorationColor: GOLD_FAINT,
    textUnderlineOffset: 4,
    letterSpacing: '0.01em',
  };
  const pill = {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    height: 40, padding: '0 18px', borderRadius: 999,
    background: 'rgba(18,12,6,0.55)',
    border: '1px solid rgba(212,170,82,0.32)',
    backdropFilter: 'blur(10px)',
    color: '#FFE7A8',
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 14, letterSpacing: '0.01em',
    textDecoration: 'none',
    transition: 'border-color 320ms ease, color 320ms ease',
  };

  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 50,
      background: scrolled ? 'rgba(16,10,6,0.66)' : 'transparent',
      backdropFilter: scrolled ? 'blur(14px)' : 'blur(0px)',
      borderBottom: scrolled ? `1px solid ${GOLD_FAINT}` : '1px solid transparent',
      padding: '14px clamp(14px, 4vw, 24px)',
      transition: 'background 480ms ease, backdrop-filter 480ms ease, border-color 480ms ease',
    }} data-testid="homepage-nav">
      <style>{`
        @media (max-width: 760px) {
          .nav-link-schools { display: none !important; }
          .nav-row { gap: 12px !important; }
        }
        @media (max-width: 520px) {
          .nav-row { gap: 10px !important; }
          .nav-signin { display: none !important; }
          .nav-cta-begin-label-full { display: none !important; }
          .nav-cta-begin-label-short { display: inline !important; }
          .nav-cta-begin { padding: 0 14px !important; height: 36px !important; font-size: 13px !important; }
        }
      `}</style>
      <div className="nav-row" style={{
        maxWidth: 1180, margin: '0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 22,
      }}>
        <Link to="/" style={{
          fontFamily: SERIF,
          fontSize: 'clamp(20px, 5vw, 24px)', fontWeight: 500, color: IVORY, textDecoration: 'none',
          letterSpacing: '0.01em', flexShrink: 0,
        }} data-testid="nav-logo">
          EventDrop <span style={{ color: GOLD, fontSize: 14, marginLeft: 2 }}>✦</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(10px, 2vw, 22px)', flexShrink: 1, minWidth: 0 }}>
          {/* iter217 — Services ▾ dropdown. Hosts the commercial
              vertical (Photobooth Hire · Venues · Recent Events ·
              For Schools) without cluttering the editorial nav. */}
          <div ref={servicesWrapRef} style={{ position: 'relative' }} className="nav-services-wrap">
            <button
              type="button"
              onClick={() => setServicesOpen(v => !v)}
              data-testid="nav-services-toggle"
              aria-expanded={servicesOpen}
              aria-haspopup="true"
              style={{
                ...quietLink,
                textDecoration: 'none',
                background: 'transparent', border: 'none',
                cursor: 'pointer', padding: 0,
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >
              Services <span style={{ color: '#D4AF5E', marginLeft: 4, fontStyle: 'normal' }}>✦</span>
              <span style={{
                fontSize: 10, color: GOLD,
                transform: servicesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 220ms ease',
              }}>▾</span>
            </button>
            {servicesOpen && (
              <div
                data-testid="nav-services-dropdown"
                style={{
                  position: 'absolute', top: 'calc(100% + 14px)', right: 0,
                  minWidth: 220,
                  background: 'rgba(20,12,6,0.92)',
                  border: `1px solid ${GOLD_FAINT}`,
                  borderRadius: 10,
                  padding: '10px 6px',
                  boxShadow: '0 22px 60px rgba(0,0,0,0.55)',
                  backdropFilter: 'blur(20px)',
                  zIndex: 60,
                }}
              >
                {[
                  { to: '/photobooth-hire', label: 'Photobooth Hire' },
                  { to: '/events',          label: 'Recent Events' },
                  { to: '/venues',          label: 'Venues We Attend' },
                  { to: '/schools',         label: 'For Schools' },
                ].map(l => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-services-link-${l.to.replace('/', '')}`}
                    style={{
                      display: 'block', padding: '10px 16px',
                      fontFamily: SERIF, fontStyle: 'italic', fontSize: 14,
                      color: IVORY, textDecoration: 'none', borderRadius: 6,
                      transition: 'background 180ms ease',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,170,82,0.10)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >{l.label}</Link>
                ))}
              </div>
            )}
          </div>
          <Link to="/login" className="nav-signin" style={quietLink} data-testid="nav-signin-link">Sign in</Link>
          <Link to="/create" data-testid="nav-cta-begin" className="nav-cta-begin" onClick={() => track('nav_cta_begin')} style={{ ...pill, whiteSpace: 'nowrap' }}>
            <span className="nav-cta-begin-label-full">Begin your evening</span>
            <span className="nav-cta-begin-label-short" style={{ display: 'none' }}>Begin</span>
            <span style={{ color: GOLD, marginLeft: 4 }}>✦</span>
          </Link>
        </div>
      </div>
    </nav>
  );
};

// =========================================================================
// SECTION 1 — HERO
// "How will tonight be remembered? ✦"
// Walnut radial haze, restrained gold drift, no particles, no parallax.
// =========================================================================
const Hero = () => {
  const reduce = useReducedMotion();
  return (
    <section data-testid="hero-section" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Atmosphere — walnut radial haze + a single faint gold drift. */}
      <HeroAtmosphere reduce={reduce} />
      <motion.div
        initial="hidden"
        animate="show"
        variants={calmStagger(0.2, 0.22)}
        style={{
          position: 'relative', zIndex: 1,
          maxWidth: 980, margin: '0 auto',
          padding: 'clamp(96px, 14vh, 168px) 24px clamp(96px, 12vh, 144px)',
          textAlign: 'center',
        }}
      >
        <motion.p variants={calmRise} style={{
          margin: 0, marginBottom: 24,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
          color: GOLD_MUTED,
        }} data-testid="hero-eyebrow">
          Booth OS ✦
        </motion.p>
        <motion.h1 variants={calmRise} style={{
          margin: 0,
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(40px, 7vw, 76px)',
          lineHeight: 1.04, letterSpacing: '-0.02em',
          color: IVORY,
          textShadow: '0 0 32px rgba(212,170,82,0.10)',
        }} data-testid="hero-headline">
          How will tonight be remembered? <span style={{ color: GOLD, fontStyle: 'normal' }}>✦</span>
        </motion.h1>
        <motion.p variants={calmRise} style={{
          margin: '32px auto 0', maxWidth: 640,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 'clamp(16px, 1.8vw, 19px)', lineHeight: 1.6,
          color: IVORY_DIM,
        }} data-testid="hero-subcopy">
          One quiet place for every photo, voice, feeling, message and memory
          the room leaves behind.
        </motion.p>
        <motion.div variants={calmRise} style={{
          marginTop: 48, display: 'flex', gap: 18,
          flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center',
        }}>
          <Link
            to="/create?edition=room"
            data-testid="hero-cta-primary"
            onClick={() => track('hero_explore_booth_os')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '16px 30px', borderRadius: 999,
              background: 'rgba(18,12,6,0.55)',
              border: `1px solid ${GOLD_MUTED}`,
              backdropFilter: 'blur(10px)',
              color: IVORY,
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 16, letterSpacing: '0.01em',
              textDecoration: 'none',
              transition: 'border-color 360ms ease, color 360ms ease',
            }}
          >
            Explore Booth OS <span style={{ color: GOLD }}>✦</span>
          </Link>
          <a
            href="#editions"
            data-testid="hero-cta-secondary"
            onClick={() => track('hero_see_editions')}
            style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 15,
              color: IVORY_DIM,
              textDecoration: 'underline',
              textDecorationColor: GOLD_FAINT,
              textUnderlineOffset: 5,
            }}
          >
            See the editions
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};

const HeroAtmosphere = ({ reduce }) => {
  return (
    <>
      {/* Walnut radial haze — primary surface. */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0,
        background:
          `radial-gradient(ellipse 90% 70% at 50% 35%, rgba(28,18,10,0.0) 0%, rgba(14,8,5,0.55) 50%, rgba(8,5,3,0.95) 95%),` +
          `radial-gradient(ellipse 60% 50% at 50% 25%, rgba(212,170,82,0.06) 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      {/* Single restrained gold drift — no particles, no parallax. */}
      <motion.div
        aria-hidden
        animate={reduce ? { opacity: 0.5 } : { opacity: [0.35, 0.6, 0.4], y: [0, -10, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute', left: '50%', top: '32%',
          transform: 'translateX(-50%)',
          width: 720, height: 320,
          maxWidth: '90vw',
          background: `radial-gradient(ellipse at center, rgba(212,170,82,0.10) 0%, transparent 65%)`,
          filter: 'blur(20px)',
          pointerEvents: 'none',
        }}
      />
    </>
  );
};

// =========================================================================
// SECTION 2 — PHILOSOPHICAL RESET
// Narrow centred. Almost no visual structure. Quietly declarative.
// =========================================================================
const PhilosophicalReset = () => (
  <section data-testid="philosophical-reset" style={{
    background: WALNUT_DEEP,
    padding: 'clamp(96px, 14vh, 160px) 24px',
  }}>
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-15%' }}
      transition={{ duration: 1.4, ease: [0.22, 0.61, 0.36, 1] }}
      style={{
        maxWidth: 560, margin: '0 auto',
        textAlign: 'center',
      }}
    >
      <p style={{
        margin: 0,
        fontFamily: SERIF,
        fontSize: 'clamp(22px, 3vw, 30px)',
        lineHeight: 1.5,
        letterSpacing: '-0.005em',
        color: IVORY,
        fontWeight: 500,
      }} data-testid="reset-copy">
        <span style={{ fontStyle: 'italic' }}>Not a gallery.</span><br />
        <span style={{ fontStyle: 'italic' }}>Not a guestbook.</span><br />
        <span style={{ display: 'inline-block', marginTop: 22 }}>
          A shared atmosphere for the evening.
        </span>
      </p>
    </motion.div>
  </section>
);

// =========================================================================
// SECTION 3 — ONE QR · THE ENTIRE EVENING
// Booth OS as the environment, not a product.
// =========================================================================
const OneQrSection = () => (
  <section data-testid="one-qr-section" style={{
    background: WALNUT,
    padding: 'clamp(96px, 14vh, 160px) 24px',
    position: 'relative',
  }}>
    <div style={{ maxWidth: 1080, margin: '0 auto' }}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-12%' }}
        variants={calmStagger(0, 0.18)}
        style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 56, alignItems: 'center' }}
      >
        <motion.div variants={calmRise} style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <p style={{
            margin: 0, marginBottom: 16,
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
            color: GOLD_MUTED,
          }} data-testid="oneqr-eyebrow">
            One QR. The entire evening.
          </p>
          <h2 style={{
            margin: 0,
            fontFamily: SERIF, fontWeight: 500,
            fontSize: 'clamp(30px, 4.4vw, 48px)',
            lineHeight: 1.12, letterSpacing: '-0.01em',
            color: IVORY,
            textShadow: '0 0 24px rgba(212,170,82,0.08)',
          }} data-testid="oneqr-heading">
            Guests scan once and quietly enter the evening.
          </h2>
          <p style={{
            margin: '28px auto 0', maxWidth: 580,
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 'clamp(15px, 1.6vw, 17px)', lineHeight: 1.7,
            color: IVORY_DIM,
          }} data-testid="oneqr-subcopy">
            Photos, handwritten video notes, guestbooks, live atmosphere,
            films and keepsakes — all from one shared place.
          </p>
        </motion.div>
        <motion.div variants={calmRise}>
          <BoothOsViewport />
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// A soft framed viewport hinting at Booth OS's four entry sections.
// Intentionally NOT a phone screenshot, NOT a feature grid — it reads
// as a quiet menu of moments the evening holds.
const BoothOsViewport = () => {
  const stops = [
    { label: 'Capture the room',     hint: 'photos · 360 · pocket booth' },
    { label: 'Leave something behind', hint: 'voice · handwriting · feeling' },
    { label: 'See the night forming',  hint: 'wall · projector · atmosphere' },
    // iter206 — the preservation hint catches up with the
    // architecturally-real artefacts: the Chronicle (Final Night
    // PDF) and the Silent Film (vertical MP4) shipped via
    // iter200b / iter201. Same density as the prior 3-item hint,
    // same lowercase atmospheric register — no labels, no `NEW`,
    // no cards. The products feel discovered, not advertised.
    { label: 'Preserve the night',     hint: 'final night · silent film · highlights · archive' },
  ];
  return (
    <div data-testid="booth-os-viewport" style={{
      maxWidth: 620, margin: '0 auto',
      padding: 'clamp(28px, 4vw, 44px)',
      background: `linear-gradient(180deg, rgba(28,18,10,0.55) 0%, rgba(14,8,5,0.7) 100%)`,
      border: `1px solid ${GOLD_FAINT}`,
      borderRadius: 18,
      boxShadow: `0 30px 90px rgba(0,0,0,0.5), 0 0 0 1px rgba(212,170,82,0.04) inset`,
      backdropFilter: 'blur(8px)',
    }}>
      <p style={{
        margin: 0, marginBottom: 20,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase',
        color: GOLD_MUTED, textAlign: 'center',
      }}>
        Inside the room ✦
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 14 }}>
        {stops.map((s, i) => (
          <li key={s.label} data-testid={`booth-os-stop-${i}`} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            gap: 16, paddingBottom: 12,
            borderBottom: i < stops.length - 1 ? `1px solid rgba(212,170,82,0.10)` : 'none',
          }}>
            <span style={{
              fontFamily: SERIF, fontSize: 18, color: IVORY, letterSpacing: '0.01em',
            }}>{s.label}<span style={{ color: GOLD, marginLeft: 8 }}>✦</span></span>
            <span style={{
              fontFamily: SERIF, fontStyle: 'italic', fontSize: 13, color: IVORY_GHOST,
              textAlign: 'right',
            }}>{s.hint}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// =========================================================================
// TRANSITION BRIDGE — implicit "room responding" thread
// =========================================================================
const TransitionBridge = ({ copy, testId }) => (
  <section data-testid={testId} style={{
    background: WALNUT,
    padding: 'clamp(64px, 9vh, 100px) 24px',
  }}>
    <motion.p
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: '-18%' }}
      transition={{ duration: 1.6, ease: 'easeOut' }}
      style={{
        margin: '0 auto', maxWidth: 560, textAlign: 'center',
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 'clamp(17px, 2vw, 21px)', lineHeight: 1.55,
        color: IVORY_DIM, letterSpacing: '0.005em',
      }}
    >
      {copy}
    </motion.p>
  </section>
);

// =========================================================================
// SECTION 4 — THE FOUR EMOTIONAL LAYERS
// Editorial grouping. No feature cards. No icons. Typography + spacing.
// =========================================================================
const LAYERS = [
  {
    id: 'capture',
    eyebrow: 'i · capture the room',
    headline: 'The night, as it happens.',
    tone: 'Live · participatory · immediate.',
    moments: ['Upload memories', 'Memory snap', 'Pocket booth', 'Spin film 360'],
  },
  {
    id: 'leave',
    eyebrow: 'ii · leave something behind',
    headline: 'Something only you could have said.',
    tone: 'Personal · emotional · human.',
    moments: ['Audio & video guestbook', 'Flip guestbook', 'Lens notes', 'Drop one word'],
  },
  {
    id: 'see',
    eyebrow: 'iii · see the night forming',
    headline: 'The room beginning to feel inhabited.',
    tone: 'Ambient · collective · alive.',
    moments: ['Memory wall', 'Live atmosphere', 'Live projector'],
  },
  {
    id: 'preserve',
    eyebrow: 'iv · preserve the night',
    headline: 'Something to return to, once the room is empty.',
    tone: 'Cinematic · lasting · archival.',
    // iter206 — moments list catches up with the artefacts that
    // actually ship: The Final Night (Chronicle, iter200b/201)
    // and Silent Film (vertical 9:16 recap, iter201). Same
    // register as before — single noun phrases, no ✦ marks, no
    // NEW labels. Restraint is the homepage's job.
    moments: ['The Final Night', 'Silent film', 'Highlight reel', 'Archive keepsakes'],
  },
];

const FourEmotionalLayers = () => (
  <section data-testid="emotional-layers-section" style={{
    background: WALNUT_ALT,
    padding: 'clamp(96px, 14vh, 160px) 24px',
  }}>
    <div style={{ maxWidth: 920, margin: '0 auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-12%' }}
        transition={{ duration: 1.1, ease: [0.22, 0.61, 0.36, 1] }}
        style={{ textAlign: 'center', marginBottom: 'clamp(72px, 10vh, 120px)' }}
      >
        <p style={{
          margin: 0, marginBottom: 14,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
          color: GOLD_MUTED,
        }}>Four atmospheres ✦</p>
        <h2 style={{
          margin: '0 auto', maxWidth: 700,
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(28px, 4vw, 40px)',
          lineHeight: 1.16, letterSpacing: '-0.01em',
          color: IVORY,
        }}>
          An evening, told in four quiet layers.
        </h2>
      </motion.div>

      <div style={{ display: 'grid', gap: 'clamp(72px, 10vh, 120px)' }}>
        {LAYERS.map((layer, i) => (
          <LayerEntry key={layer.id} layer={layer} index={i} />
        ))}
      </div>
    </div>
  </section>
);

const LayerEntry = ({ layer, index }) => {
  const flip = index % 2 === 1; // alternating left/right asymmetry
  return (
    <motion.article
      data-testid={`layer-${layer.id}`}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-15%' }}
      transition={{ duration: 1.0, ease: [0.22, 0.61, 0.36, 1] }}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: 28,
        maxWidth: 820, margin: '0 auto',
        textAlign: flip ? 'right' : 'left',
      }}
    >
      <p style={{
        margin: 0,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
        color: GOLD_MUTED,
      }}>{layer.eyebrow}</p>
      <h3 style={{
        margin: 0,
        fontFamily: SERIF, fontWeight: 500,
        fontSize: 'clamp(26px, 3.6vw, 38px)',
        lineHeight: 1.18, letterSpacing: '-0.01em',
        color: IVORY,
        maxWidth: 720,
        marginLeft: flip ? 'auto' : 0,
        marginRight: flip ? 0 : 'auto',
      }}>
        {layer.headline}
      </h3>
      <p style={{
        margin: 0,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase',
        color: IVORY_GHOST,
      }}>{layer.tone}</p>
      {/* Quiet enumeration — no icons, no cards, just a comma-spaced
          line of italic moments. This is intentionally minimal. */}
      <p style={{
        margin: 0, maxWidth: 720,
        marginLeft: flip ? 'auto' : 0,
        marginRight: flip ? 0 : 'auto',
        fontFamily: SERIF,
        fontSize: 'clamp(15px, 1.6vw, 17px)', lineHeight: 1.85,
        color: IVORY_DIM,
        letterSpacing: '0.01em',
      }}>
        {layer.moments.map((m, mi) => (
          <span key={m}>
            {m}
            {mi < layer.moments.length - 1 && (
              <span style={{ color: GOLD_FAINT, margin: '0 12px' }}>·</span>
            )}
          </span>
        ))}
      </p>
    </motion.article>
  );
};

// =========================================================================
// SECTION 5 — EDITIONS · MOODS, NOT TIERS
// Shared PackageFrame, distinct internals.
// =========================================================================
const Packages = () => (
  <section id="editions" data-testid="packages-section" style={{
    background: WALNUT,
    padding: 'clamp(96px, 14vh, 160px) 24px',
  }}>
    <div style={{ maxWidth: 1180, margin: '0 auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-10%' }}
        transition={{ duration: 1.1, ease: [0.22, 0.61, 0.36, 1] }}
        style={{ textAlign: 'center', marginBottom: 'clamp(64px, 9vh, 96px)' }}
      >
        <p style={{
          margin: 0, marginBottom: 14,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
          color: GOLD_MUTED,
        }}>Editions ✦</p>
        <h2 style={{
          margin: '0 auto', maxWidth: 720,
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(28px, 4vw, 40px)',
          lineHeight: 1.16, letterSpacing: '-0.01em',
          color: IVORY,
        }}>
          Three scales of evening.
        </h2>
      </motion.div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 'clamp(24px, 3vw, 40px)',
        alignItems: 'stretch',
      }}>
        <KeepsakeMood pkg={PACKAGES[0]} />
        <RoomMood pkg={PACKAGES[1]} />
        <ArchiveMood pkg={PACKAGES[2]} />
      </div>
    </div>
  </section>
);

// ── Shared frame — same outer shell, distinct interiors per package ──
const PackageFrame = ({ mood, accent, glow, paddingScale, children, testId }) => {
  const padding = {
    intimate:   'clamp(28px, 3vw, 36px)',
    social:     'clamp(36px, 4vw, 48px)',
    cinematic:  'clamp(44px, 5vw, 60px)',
  }[paddingScale];
  return (
    <article data-testid={testId} data-mood={mood} style={{
      position: 'relative',
      padding,
      background: `linear-gradient(180deg, ${accent.surfaceTop} 0%, ${accent.surfaceBottom} 100%)`,
      border: `1px solid ${accent.border}`,
      borderRadius: 18,
      boxShadow: `0 30px 90px rgba(0,0,0,0.45), 0 0 ${glow.spread} ${glow.color} inset`,
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
    }}>
      {children}
    </article>
  );
};

// THE KEEPSAKE ✦ — intimate · light · quiet · personal
const KeepsakeMood = ({ pkg }) => (
  <PackageFrame
    testId="package-keepsake"
    mood="intimate"
    paddingScale="intimate"
    accent={{
      surfaceTop: 'rgba(20,14,8,0.55)',
      surfaceBottom: 'rgba(12,8,5,0.65)',
      border: 'rgba(212,170,82,0.18)',
    }}
    glow={{ spread: '40px', color: 'rgba(212,170,82,0.04)' }}
  >
    <header>
      <p style={moodEyebrow}>{pkg.name}</p>
      <h3 style={{ ...moodHeading, fontSize: 'clamp(24px, 3vw, 30px)' }}>
        {pkg.positioning}
      </h3>
      <p style={{ ...moodCaption, fontSize: 14, marginTop: 16 }}>
        {pkg.caption}
      </p>
    </header>
    <ul style={{ ...moodList, marginTop: 28, lineHeight: 2.0 }}>
      {pkg.items.map((m) => (
        <li key={m} style={moodListItem}>{m}</li>
      ))}
    </ul>
    <MoodFooter editionId={pkg.id} price={pkg.price} cta={pkg.cta} testId="package-keepsake-cta" event="package_keepsake" muted />
  </PackageFrame>
);

// THE ROOM ✦ — social · participatory · alive · collective
const RoomMood = ({ pkg }) => (
  <PackageFrame
    testId="package-room"
    mood="social"
    paddingScale="social"
    accent={{
      surfaceTop: 'rgba(28,18,10,0.65)',
      surfaceBottom: 'rgba(14,9,5,0.75)',
      border: 'rgba(212,170,82,0.30)',
    }}
    glow={{ spread: '70px', color: 'rgba(212,170,82,0.07)' }}
  >
    <header>
      <p style={moodEyebrow}>{pkg.name}</p>
      <h3 style={{ ...moodHeading, fontSize: 'clamp(28px, 3.4vw, 36px)' }}>
        {pkg.positioning}
      </h3>
      <p style={{ ...moodCaption, fontSize: 15, marginTop: 18 }}>
        <span style={{ fontStyle: 'normal', color: GOLD }}>{pkg.caption}</span>
      </p>
    </header>
    <div style={{ marginTop: 32 }}>
      <p style={{ ...moodGroupEyebrow }}>Inside The Room</p>
      <ul style={{ ...moodList, lineHeight: 2.1 }}>
        {pkg.items.map((m) => (
          <li key={m} style={moodListItem}>{m}</li>
        ))}
      </ul>
      <p style={{ ...moodGroupEyebrow, marginTop: 26 }}>Available inside The Archive ✦</p>
      <ul style={{ ...moodList, lineHeight: 2.0, opacity: 0.45 }}>
        {pkg.dimmed.map((m) => (
          <li key={m} style={moodListItem}>{m}</li>
        ))}
      </ul>
    </div>
    <MoodFooter editionId={pkg.id} price={pkg.price} cta={pkg.cta} testId="package-room-cta" event="package_room" />
  </PackageFrame>
);

// THE ARCHIVE ✦ — cinematic · emotionally complete · preserving
const ArchiveMood = ({ pkg }) => (
  <PackageFrame
    testId="package-archive"
    mood="cinematic"
    paddingScale="cinematic"
    accent={{
      surfaceTop: 'rgba(34,22,12,0.75)',
      surfaceBottom: 'rgba(16,10,6,0.85)',
      border: 'rgba(212,170,82,0.42)',
    }}
    glow={{ spread: '110px', color: 'rgba(212,170,82,0.10)' }}
  >
    <header>
      <p style={{ ...moodEyebrow, color: GOLD }}>{pkg.name}</p>
      <h3 style={{ ...moodHeading, fontSize: 'clamp(30px, 3.8vw, 42px)', lineHeight: 1.12 }}>
        {pkg.positioning}
      </h3>
      <p style={{ ...moodCaption, fontSize: 16, marginTop: 22, lineHeight: 1.65 }}>
        {pkg.caption}
      </p>
    </header>
    <ul style={{ ...moodList, marginTop: 36, lineHeight: 2.2 }}>
      {pkg.items.map((m) => (
        <li key={m} style={moodListItem}>{m}</li>
      ))}
    </ul>
    <MoodFooter editionId={pkg.id} price={pkg.price} cta={pkg.cta} testId="package-archive-cta" event="package_archive" flagship />
  </PackageFrame>
);

const MoodFooter = ({ editionId, price, cta, testId, event, muted = false, flagship = false }) => (
  <footer style={{
    marginTop: flagship ? 44 : (muted ? 28 : 36),
    paddingTop: 24,
    borderTop: `1px solid ${GOLD_FAINT}`,
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
  }}>
    <p style={{
      margin: 0,
      fontFamily: SERIF,
      fontSize: flagship ? 'clamp(28px, 3.6vw, 36px)' : 'clamp(22px, 2.6vw, 28px)',
      color: flagship ? GOLD : IVORY,
      letterSpacing: '-0.01em',
    }}>{price}</p>
    <Link to={`/create?edition=${editionId}`} data-testid={testId} onClick={() => track(event)} style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: flagship ? '14px 26px' : (muted ? '10px 20px' : '12px 22px'),
      borderRadius: 999,
      background: flagship ? 'rgba(212,170,82,0.10)' : 'transparent',
      border: `1px solid ${flagship ? GOLD : GOLD_MUTED}`,
      color: flagship ? IVORY : IVORY_DIM,
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: flagship ? 15 : (muted ? 13 : 14),
      textDecoration: 'none',
      letterSpacing: '0.01em',
      transition: 'border-color 360ms ease, color 360ms ease',
    }}>
      {cta} <span style={{ color: GOLD, marginLeft: 4 }}>✦</span>
    </Link>
  </footer>
);

// Shared text tokens for package interiors
const moodEyebrow = {
  margin: 0,
  fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 12, letterSpacing: '0.24em', textTransform: 'uppercase',
  color: GOLD_MUTED,
};
const moodHeading = {
  margin: '14px 0 0',
  fontFamily: SERIF, fontWeight: 500,
  lineHeight: 1.2, letterSpacing: '-0.01em',
  color: IVORY,
};
const moodCaption = {
  margin: 0,
  fontFamily: SERIF, fontStyle: 'italic',
  color: IVORY_DIM,
};
const moodGroupEyebrow = {
  margin: '0 0 10px',
  fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 11, letterSpacing: '0.20em', textTransform: 'uppercase',
  color: GOLD_MUTED,
};
const moodList = {
  listStyle: 'none', margin: 0, padding: 0,
  fontFamily: SERIF,
  color: IVORY_DIM,
};
const moodListItem = {
  fontSize: 15, letterSpacing: '0.01em',
};

// =========================================================================
// SCHOOLS BRIDGE — quiet editorial discovery (iter193 follow-on)
// Not a fourth package. Not equal weighting. Not "pick your sector."
// One restrained moment that lets Leavers Night ✦ be DISCOVERED rather
// than merchandised. Sits between editions and the final CTA.
// Per belief 5 (different emotional centres) and belief 6 (luxury
// implies depth — do not over-explain).
// =========================================================================
const SchoolsBridge = () => (
  <section data-testid="schools-bridge" style={{
    background: WALNUT,
    padding: 'clamp(72px, 10vh, 112px) 24px',
  }}>
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-12%' }}
      transition={{ duration: 1.2, ease: [0.22, 0.61, 0.36, 1] }}
      style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}
    >
      <p style={{
        margin: 0, marginBottom: 22,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
        color: GOLD_MUTED,
      }} data-testid="schools-bridge-eyebrow">
        For schools ✦
      </p>
      <p style={{
        margin: 0,
        fontFamily: SERIF,
        fontSize: 'clamp(20px, 2.4vw, 24px)', lineHeight: 1.45, letterSpacing: '-0.005em',
        color: IVORY, fontWeight: 500,
      }} data-testid="schools-bridge-headline">
        Leavers Night <span style={{ color: GOLD }}>✦</span>
      </p>
      <p style={{
        margin: '20px auto 0', maxWidth: 500,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 'clamp(15px, 1.6vw, 17px)', lineHeight: 1.7,
        color: IVORY_DIM,
      }} data-testid="schools-bridge-body">
        Leavers Night transforms Booth OS into a shared year-group experience
        built for Year 6 and Year 11/13 leavers nights.
      </p>
      <p style={{
        margin: '24px auto 0', maxWidth: 500,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 14, lineHeight: 1.75,
        color: IVORY_GHOST, letterSpacing: '0.01em',
      }} data-testid="schools-bridge-moments">
        Remember Us <span style={{ color: GOLD, fontStyle: 'normal' }}>✦</span>
        <span style={{ color: GOLD_FAINT, margin: '0 10px' }}>·</span>
        Pocket Booth
        <span style={{ color: GOLD_FAINT, margin: '0 10px' }}>·</span>
        Lens Notes
        <span style={{ color: GOLD_FAINT, margin: '0 10px' }}>·</span>
        Memory Wall
        <span style={{ color: GOLD_FAINT, margin: '0 10px' }}>·</span>
        Live projector atmosphere
      </p>
      <p style={{
        margin: '32px 0 0',
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 13, letterSpacing: '0.18em', textTransform: 'uppercase',
        color: GOLD_MUTED,
      }} data-testid="schools-bridge-price-line">
        One package <span style={{ color: GOLD_FAINT, margin: '0 8px' }}>·</span> £99
      </p>
      <div style={{ marginTop: 32 }}>
        <Link
          to="/schools"
          data-testid="schools-bridge-cta"
          onClick={() => track('homepage_schools_bridge')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 15, color: IVORY_DIM,
            textDecoration: 'underline',
            textDecorationColor: GOLD_FAINT,
            textUnderlineOffset: 5,
            transition: 'color 320ms ease, text-decoration-color 320ms ease',
          }}
        >
          Explore Leavers Night <span style={{ color: GOLD }}>✦</span>
        </Link>
      </div>
    </motion.div>
  </section>
);

// =========================================================================
// GLOBAL STYLES — keep tiny, only what the page itself depends on
// =========================================================================
const GlobalStyles = () => (
  <style>{`
    /* Editorial selection — gold tint on text-select. */
    [data-testid="homepage-root"] ::selection {
      background: rgba(212,170,82,0.22);
      color: #FFF5DA;
    }
    /* Honour reduced-motion users everywhere. */
    @media (prefers-reduced-motion: reduce) {
      [data-testid="homepage-root"] * {
        animation-duration: 0.001ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.001ms !important;
      }
    }
  `}</style>
);
